import PropTypes from 'prop-types'
import React from 'react'
import { findDOMNode } from 'react-dom'
import { FormattedMessage } from 'react-intl'
import * as ActivityDetailLevel from 'const/ActivityDetailLevel'
import InfiniteScroll from 'components/InfiniteScroll'
import ActivityGroup from 'components/ActivityGroup'
import BusyOverlay from 'components/BusyOverlay'
import scrollIntoView from 'scroll-into-view-if-needed'
import { uncontrollable } from 'uncontrollable'
import classes from './Activities.module.scss'
import { connect } from 'react-redux'
import activitiesSelector from './activitiesSelector'

type OwnProps = {
  context: string
  activities: any[]
  activityComponent: React.ComponentType<any>
  isLoading: boolean
  isLoaded: boolean
  lastLoadedId?: number | string
  fetchActivities: ({ lastLoadedId }) => void
  currentUser: any,
  focusActivity?: number
  detailLevel: number
  onDetailLevelChange: (detailLevel: number) => void
}

type StateProps = {
  timeGroups: any[]
}

type Props = OwnProps & StateProps

type State = {
  focusedActivity?: number
  activityWasFocused: boolean
}

export class Activities extends React.Component<Props, State> {
  static defaultProps = {
    activities: [],
    activityComponent: ActivityGroup,
    detailLevel: ActivityDetailLevel.ALL
  }

  state = {
    focusedActivity: undefined,
    activityWasFocused: false
  }

  activityRefs = {}

  componentDidMount () {
    this.checkFocusActivity()
  }

  componentDidUpdate (prevProps, prevState) {
    this.checkFocusActivity()

    if (
      this.state.focusedActivity &&
      this.state.activityWasFocused &&
      !prevState.activityWasFocused
    ) {
      this.scrollToActivity(this.state.focusedActivity)
    }
  }

  scrollToActivity = (id) => {
    requestAnimationFrame(() => {
      const activityRef = this.activityRefs[id]
      if (activityRef) {
        const activityDOMNode = findDOMNode(activityRef)
        if (activityDOMNode) {
          scrollIntoView(activityDOMNode as Element, {
            scrollMode: 'if-needed',
            block: 'nearest'
          })
        }
      }
    })
  }

  checkFocusActivity = () => {
    const {
      activities,
      isLoading,
      isLoaded,
      detailLevel,
      focusActivity,
      onDetailLevelChange
    } = this.props
    const {
      focusedActivity,
      activityWasFocused
    } = this.state

    if (focusActivity) {
      if (
        !focusedActivity ||
        focusedActivity !== focusActivity
      ) { // New activity to focus
        this.setState({
          focusedActivity: focusActivity,
          activityWasFocused: false
        })
      } else if (!activityWasFocused) { // Start searching activity
        const results = activities.filter(activity => activity.id === focusedActivity)

        const activity = results[0]

        if (activity) { // Activity found
          if (activity.tags) {
            let activityDetailLevel = ActivityDetailLevel.ALL
            if (activity.tags.indexOf('important') !== -1) activityDetailLevel = ActivityDetailLevel.IMPORTANT
            if (activity.tags.indexOf('message') !== -1) activityDetailLevel = ActivityDetailLevel.COMMENTS

            if (activityDetailLevel > detailLevel) { // Toggle detail level so activity can be dispalyed
              onDetailLevelChange(activityDetailLevel)
            }
          }

          this.setState({
            activityWasFocused: true
          })
        } else if (!isLoaded) { // Try loading more activities
          if (!isLoading) {
            this.loadActivities()
          }
        } else { // End of activities. Activity not found.
          this.setState({
            activityWasFocused: true
          })
        }
      }
    } else { // Clear activity to focus
      if (focusedActivity) {
        this.setState({
          focusedActivity: undefined,
          activityWasFocused: false
        })
      }
    }
  }

  loadActivities = () => {
    const {
      lastLoadedId,
      fetchActivities
    } = this.props

    fetchActivities({ lastLoadedId })
  }

  renderTimeGroup = (timeGroup) => {
    return (
      <div className={classes.timeGroup}>
        <div className={classes.timeGroupHeader}>
          <FormattedMessage id={timeGroup.formattedMessageId} />
        </div>
        {timeGroup.activityGroups.map(activityGroup => this.renderActivityGroup(activityGroup))}
      </div>
    )
  }

  renderActivityGroup = (activityGroup) => {
    const {
      context,
      currentUser,
      activityComponent: ActivityComponent
    } = this.props
    const {
      focusedActivity,
      activityWasFocused
    } = this.state

    return (
      <ActivityComponent
        {...activityGroup}
        key={activityGroup.key}
        activityRef={(ref, id) => {
          if (ref) {
            this.activityRefs[id] = ref
          } else if (this.activityRefs[id]) {
            delete this.activityRefs[id]
          }
        }}
        context={context}
        currentUser={currentUser}
        focusedActivity={focusedActivity}
        activityWasFocused={activityWasFocused}
      />
    )
  }

  render () {
    const {
      isLoaded,
      isLoading,
      focusActivity,

      timeGroups,

      ...props
    } = this.props
    const {
      focusedActivity,
      activityWasFocused
    } = this.state

    return (
      <InfiniteScroll
        isLoading={isLoading}
        isLoaded={isLoaded}
        onLoadMore={this.loadActivities}
      >
        {timeGroups.map(timeGroup => this.renderTimeGroup(timeGroup))}
        {(focusedActivity && !activityWasFocused) &&
          <BusyOverlay />
        }
      </InfiniteScroll>
    )
  }
}


export default uncontrollable(
  connect(
    activitiesSelector,
    {},
    null,
    {
      forwardRef: true
    }
  )(Activities),
  {
    detailLevel: 'onDetailLevelChange'
  }
)
